import type { TFunction } from 'i18next';

import type { FilterDef, FilterOption } from '@nucleus-commerce/admin-kit/index-table';
import {
  SeoEntityType,
  SeoIssueCode,
  SeoIssueSeverity,
  type SeoIssue,
} from '@nucleus-commerce/api-client/admin';

/** Fields GET /seo/issues sorts on; anything else is a 400. Severity sorts by weight. */
export const SEO_ISSUE_SORTS = ['severity', 'lastSeenAt'] as const;

/**
 * Issue states the list filters on. The API also takes `all`, which is the same as both; without
 * a status it lists open issues only.
 */
export const SEO_ISSUE_STATUSES = ['open', 'resolved'] as const;

const valuesOf = (values: readonly string[]): FilterOption[] =>
  values.map((value) => ({ value, label: value }));

/**
 * The filters as the URL and the API spell them, for the route's search schema and loader. The
 * enum `options` drop unknown values the same way the page's filters do, so the prefetch and the
 * page share a query key.
 */
export const SEO_ISSUE_FILTER_FIELDS = [
  { key: 'status', type: 'enum', options: valuesOf(SEO_ISSUE_STATUSES) },
  { key: 'severity', type: 'enum', options: valuesOf(Object.values(SeoIssueSeverity)) },
  { key: 'code', type: 'enum', options: valuesOf(Object.values(SeoIssueCode)) },
  { key: 'entityType', type: 'enum', options: valuesOf(Object.values(SeoEntityType)) },
] as const;

/** Filters of the SEO issues table, in the order the filter menu lists them. `t` is `seo`. */
export function seoIssueFilters(t: TFunction): Array<FilterDef<SeoIssue>> {
  const options = (values: readonly string[], prefix: string): FilterOption[] =>
    values.map((value) => ({ value, label: t(`${prefix}.${value}`) }));

  return [
    {
      key: 'status',
      label: t('filters.status'),
      type: 'enum',
      pinned: true,
      options: options(SEO_ISSUE_STATUSES, 'issueStatus'),
    },
    {
      key: 'severity',
      label: t('filters.severity'),
      type: 'enum',
      pinned: true,
      options: options(Object.values(SeoIssueSeverity), 'severity'),
    },
    {
      key: 'code',
      label: t('filters.code'),
      type: 'enum',
      aliases: ['issue'],
      options: options(Object.values(SeoIssueCode), 'code'),
    },
    {
      key: 'entityType',
      label: t('filters.entityType'),
      type: 'enum',
      aliases: ['where'],
      options: options(Object.values(SeoEntityType), 'entityType'),
    },
  ];
}
